/**
 * Opening a report on the current month and year.
 *
 * v1 did this and v2 did not, which is why reports opened showing whatever
 * period the author last published — usually months out of date, and the sort
 * of difference that reads as "the new portal has lost my data".
 *
 * THE RULE
 *
 * For a slicer whose column is called `month` or `year`, and which ALREADY has
 * a selection, replace that selection with the current period. Everything else
 * is left exactly as the report author set it.
 *
 * "Already has a selection" is the part worth keeping. A slicer showing every
 * value is a deliberate choice by whoever built the report, and narrowing it
 * to one month on the user's behalf would be inventing a filter that was never
 * there. v1 had this condition (`&& value`) and it was right.
 *
 * MATCHING THE MODEL'S OWN SHAPE
 *
 * Power BI's `In` operator compares values exactly, so "August" will not match
 * a model that stores "Aug", and 2026 will not match "2026". The only reliable
 * guide to which a model uses is what the slicer is showing right now, so the
 * replacement is built to match the shape of the value being replaced.
 *
 * Two cases where v1 got that wrong, and this deliberately does not:
 *
 *   - a NUMERIC month (1-12) fell through v1's `if (monthNamesLong.includes)`
 *     to the else branch and was set to "Aug", which matches no row. The
 *     report came up blank and looked like a data problem.
 *   - a FISCAL year ("2025/26", "FY25") is a string, so v1 replaced it with
 *     "2026" — same outcome.
 *
 * Both now return null, which leaves the slicer untouched. Showing last year's
 * figures is a small annoyance; showing an empty report is a support call, and
 * the empty report gives no clue that a slicer caused it.
 *
 * Pure, and separate from the embed hook, because every one of the cases above
 * is a value-shape question that can be settled in a test rather than by
 * opening a report and looking at it.
 */

export const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * The slicer state to apply for the current period, or null to leave it alone.
 *
 * @param {object} slicerState the result of visual.getSlicerState()
 * @param {Date}   now         injected so the behaviour is testable in January
 *                             and December without waiting for them
 */
export function currentPeriodState(slicerState, now = new Date()) {
  /* More than one target is a HIERARCHY slicer - Year > Month in one tree -
     and it takes a different filter entirely. */
  const targets = slicerState?.targets ?? [];
  if (targets.length > 1) return hierarchyPeriodState(targets, now);

  const table = targets[0]?.table;
  const column = targets[0]?.column;
  if (!table || !column) return null;

  /* `values` is absent entirely on an unfiltered slicer, hence the optional
     chain rather than an index. */
  const current = slicerState?.filters?.[0]?.values?.[0];
  const empty = current === undefined || current === null || current === '';

  const values = empty
    ? candidatesFor(String(column).toLowerCase(), now)
    : replacementFor(String(column).toLowerCase(), current, now);
  if (!values) return null;

  return {
    filters: [{
      $schema: 'http://powerbi.com/product/schema#basic',
      target: { table, column },
      operator: 'In',
      values
    }]
  };
}

/**
 * A Year ▸ Month hierarchy slicer, which takes an IHierarchyFilter.
 *
 * Not a basic filter with two targets. The shape below is taken from
 * `powerbi-models` in this repository's own node_modules rather than from
 * documentation, because Microsoft's search result for "hierarchy filter" is
 * the *custom visual* identity API - a different interface, with opaque
 * identities instead of values - and building from it would have produced
 * something that could never work:
 *
 *   models.d.ts  IHierarchyFilterNode { value?, keyValues?, children?, operator? }
 *   models.js    HierarchyFilter.schemaUrl = ".../schema#hierarchy"
 *                FilterType.Hierarchy = 9
 *   validator    hierarchyData is required; filterType, if present, must be 9;
 *                every node field is optional, so { value, operator } is valid
 *
 * Operators follow the documented rule: interior nodes are `Inherited`, and
 * every leaf is `Selected` or `NotSelected`.
 *
 * SCOPE
 *
 * Exactly two levels, year then month, in that order. A three-level hierarchy
 * (Year ▸ Quarter ▸ Month) needs every quarter present as an interior node to
 * reach the months, and a hierarchy in the other order is not a shape anyone
 * has actually reported. Both are skipped and logged rather than approximated
 * - an approximate hierarchy filter selects the wrong data points, which is
 * worse than selecting none.
 *
 * THE PART THAT IS STILL A GUESS
 *
 * The node VALUES. An empty slicer exposes no example to copy, and unlike a
 * basic filter there is no way to ask. So both year encodings appear as
 * sibling roots and all three month encodings as siblings beneath each: a
 * model holds one of them, that one matches, and the rest name data points
 * that do not exist. If Power BI rejects the unknown nodes rather than
 * ignoring them the call throws, the caller catches it, and the slicer is left
 * exactly as it was.
 */
function hierarchyPeriodState(targets, now) {
  if (targets.length !== 2) return null;

  const [outer, inner] = targets;
  if (!outer?.table || !outer?.column || !inner?.table || !inner?.column) return null;
  if (String(outer.column).toLowerCase() !== 'year') return null;
  if (String(inner.column).toLowerCase() !== 'month') return null;

  const months = candidatesFor('month', now)
    .map(value => ({ value, operator: 'Selected' }));

  return {
    filters: [{
      $schema: 'http://powerbi.com/product/schema#hierarchy',
      target: [
        { table: outer.table, column: outer.column },
        { table: inner.table, column: inner.column }
      ],
      filterType: 9,
      hierarchyData: candidatesFor('year', now).map(value => ({
        value,
        operator: 'Inherited',
        // A fresh copy per branch: shared node objects in a tree Power BI may
        // mutate is a bug waiting for a reason to appear.
        children: months.map(m => ({ ...m }))
      }))
    }]
  };
}

/**
 * Every encoding of the current period, for a slicer with nothing selected.
 *
 * An empty slicer gives no clue how its model stores the value, and that clue
 * is what makes the replacement path safe. Rather than pick one and be wrong
 * eleven months in twelve, offer them all: `In` matches any value in the list,
 * a column holds exactly one of these encodings, so exactly one candidate
 * matches and the rest are simply absent from the column.
 *
 * If Power BI refuses the mixed-type list outright, setSlicerState throws, the
 * caller catches it and the slicer is left as it was. That is the failure mode
 * worth having: unchanged rather than empty.
 *
 * Not used when the slicer already has a selection. There the existing value
 * is better evidence than any guess, and a single exact value is sent.
 */
function candidatesFor(column, now) {
  if (column === 'month') {
    return [MONTHS_LONG[now.getMonth()], MONTHS_SHORT[now.getMonth()], now.getMonth() + 1];
  }
  if (column === 'year') {
    return [now.getFullYear(), String(now.getFullYear())];
  }
  return null;
}

function replacementFor(column, current, now) {
  if (column === 'month') {
    // Long before short: "May" and "Jun" are in both lists, so a model using
    // long names would otherwise be matched as short in five months of twelve.
    if (MONTHS_LONG.includes(current)) return [MONTHS_LONG[now.getMonth()]];
    if (MONTHS_SHORT.includes(current)) return [MONTHS_SHORT[now.getMonth()]];
    // 1-12, as a number. Not 0-based: no Power BI model numbers months from
    // zero, and JavaScript is alone in doing so.
    if (typeof current === 'number') return [now.getMonth() + 1];
    // Anything else - "2026-08", "P08", a locale we do not know - is left as
    // it is rather than guessed at.
    return null;
  }

  if (column === 'year') {
    if (typeof current === 'number') return [now.getFullYear()];
    // Exactly four digits. "2025/26" and "FY25" are fiscal years whose current
    // equivalent cannot be derived from a calendar date without knowing where
    // the year starts, so they are not touched.
    if (typeof current === 'string' && /^\d{4}$/.test(current)) {
      return [String(now.getFullYear())];
    }
    return null;
  }

  return null;
}
